import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import MonitorService from 'services/monitor';

import useInterval from "hooks/useInterval";

import { Row, Col } from 'components/layout';
import { Text } from 'components/Text';
import Animate, { FadeIn } from 'components/Animate';

import Board from '../components/MonitorBoard2';
import DcStats from '../components/DcStats';
import LiveViewCameras from '../components/LiveViewCameras';
import StatsSummaryTable from '../components/StatsSummary';

import { filterDoorsAndSummary2 } from '../helpers';

const DATA_UPDATE_INTERVAL = 3 * 60 * 1000;
const ALERTS_UPDATE_INTERVAL = 60 * 1000;

const DoorsMonitor2 = ({ hardwareId, workGroup, zoneName, setMonitorInfo }) => {
  const monitorService = useMemo(() => new MonitorService(), []);
  const currentVersionRef = useRef(null);

  const [doorsStats, setDoorsStats] = useState({});
  const [alerts, setAlerts] = useState({ data: [], triggered: 0 });

  const loadingBoardData = useMemo(() => {
    const { doors, summary_loading } = doorsStats;
    return filterDoorsAndSummary2(doors, summary_loading, 'LOAD');
  }, [doorsStats]);

  const unloadingBoardData = useMemo(() => {
    const { doors, summary_unloading } = doorsStats;
    return filterDoorsAndSummary2(doors, summary_unloading, 'UNLOAD');
  }, [doorsStats]);

  const zonesDoorsPairs = useMemo(() => {
    const { doors } = doorsStats;

    const mappedByZone = (doors || []).reduce((mapped, door) => {
      mapped[door.zone_name] = mapped[door.zone_name] || [];
      mapped[door.zone_name].push(door);
      return mapped;
    }, {});

    return Object.entries(mappedByZone);
  }, [doorsStats]);

  const activeAlerts = useMemo(() => {
    return alerts.data.slice(alerts.triggered);
  }, [alerts]);

  const getDoorStats = useCallback(async () => {
    const doorStats = await monitorService.getLiveDoors({ hw_id: hardwareId, zone_name: zoneName, work_group: workGroup });
    if (!doorStats) {
      return;
    }

    const newUiVersion = doorStats.doors[0]?.ui_version;

    if (currentVersionRef.current && newUiVersion !== currentVersionRef.current) {
      window.location.reload();
    }

    currentVersionRef.current = newUiVersion;
    setDoorsStats(doorStats);
    
    if (doorStats.doors.length > 0) {
      const { location_name, timezone_offset } = doorStats.doors[0];
      setMonitorInfo({
        locationName: location_name,
        tzOffset: timezone_offset
      });
    }
  }, [monitorService, hardwareId, zoneName, workGroup, setMonitorInfo]);

  const getAlerts = useCallback(async () => {
    const alerts = await monitorService.getAlerts({ hw_id: hardwareId });
    if (!alerts) {
      return;
    }

    const now = Date.now();
    const newAlerts = alerts.filter(n => {
      return Date.parse(n.date_created) >= now - (ALERTS_UPDATE_INTERVAL);
    }).map(n => n.door_id);

    setAlerts(alerts => ({
      data: [...alerts.data, ...newAlerts],
      triggered: alerts.data.length,
    }));
  }, [monitorService, hardwareId]);

  useEffect(() => {
    getDoorStats();
  }, [getDoorStats]);

  useEffect(() => {
    getAlerts();
  }, [getAlerts]);

  useInterval(getDoorStats, DATA_UPDATE_INTERVAL);

  useInterval(getAlerts, ALERTS_UPDATE_INTERVAL, [monitorService]);

  return (
    <Animate animation={FadeIn} duration="1s" timingFunction="linear" style={{ height: '100%' }}>
      <Row style={{ height: '100%' }}>
        <Col span={5}>
          <SideBarWrapper>
            <DcStats summary={doorsStats.summary} style={{ paddingBottom: '3.6vh' }} />
            <LiveViewCameras
              doors={doorsStats.doors}
              doorsView={doorsStats.screenshots}
              doorNameField="display_name"
            />
          </SideBarWrapper>
        </Col>
        <Col span={19} style={{ borderLeft: '1px solid #DAE0E6', padding: '1.5vw 2vw' }}>
          <RowSection>
            <Col span={24}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Row>
                    <Title>Inbound</Title>
                  </Row>
                  <Row>
                    <TitleOutlined>Outbound</TitleOutlined>
                  </Row>
                </Col>
                <Col>
                  <StatsSummaryTable
                    unloadingStats={unloadingBoardData.stats}
                    loadingStats={loadingBoardData.stats}
                    emptyValue={unloadingBoardData.summary.empty + loadingBoardData.summary.empty}
                  />
                </Col>
              </Row>
            </Col>
          </RowSection>
          {zonesDoorsPairs.map(([zone, doors]) => (
            <RowSection key={zone}>
              <Col span={24}>
                <Board zone={zone} doors={doors} alerts={activeAlerts} />
              </Col>
            </RowSection>
          ))}
        </Col>
      </Row>
    </Animate>
  );
};

const RowSection = styled(Row)`
  :not(:last-child) {
    padding-bottom: 4vh;
  }
`;

const SideBarWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: #EFF2F5;
`;

const TitleBase = styled(Text)`
  width: 100%;
  padding: 0.3vw;
  border-radius: 0.5vw;
  margin-top: 0.2vw;
  margin-bottom: 0.2vw;
  font-size: 1.7vw;
  line-height: 3vh;
  font-weight: 600;
`;

const Title = styled(TitleBase)`
  color: #fff;
  background: #000;
  padding: 0.6vw;
`;

const TitleOutlined = styled(TitleBase)`
  background: #fff;
  color: #000;
  border: 0.3vw solid #000;
`;

export default DoorsMonitor2;